<template>
  <section class="d-grid gap-4">
    <header class="d-flex justify-content-between align-items-start flex-wrap gap-3">
      <div>
        <h2 class="h4 mb-1">Facturación electrónica — atención y acciones</h2>
        <p class="text-muted mb-0">Ventas fiscales que requieren atención, e inutilización/cancel-send manuales.</p>
      </div>
      <button class="btn btn-outline-dark btn-sm" @click="loadAtencion" :disabled="loadingAtencion">Recargar</button>
    </header>

    <div v-if="errorMessage" class="alert alert-danger mb-0">{{ errorMessage }}</div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6 mb-3">Ventas que requieren atención fiscal</h3>
        <div v-if="loadingAtencion" class="text-muted">Cargando...</div>
        <div v-else-if="!atencion.length" class="text-muted">No hay ventas pendientes de atención.</div>
        <div v-else class="table-responsive">
          <table class="table table-sm align-middle">
            <thead>
              <tr>
                <th>Venta</th>
                <th>N° Factura</th>
                <th>CDC</th>
                <th>Estado</th>
                <th>Motivo</th>
                <th>Detectado</th>
                <th>Backoffice FE</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="row in atencion" :key="row.id_venta">
                <td>{{ row.id_venta }}</td>
                <td>{{ row.nro_factura }}</td>
                <td class="text-truncate" style="max-width: 220px">{{ row.cdc || '—' }}</td>
                <td><span class="badge bg-danger">{{ row.fiscal_status }}</span></td>
                <td>{{ row.fiscal_atencion_motivo || '—' }}</td>
                <td>{{ formatDate(row.fiscal_atencion_detectado_en) }}</td>
                <td>
                  <a v-if="row.backoffice_url" :href="row.backoffice_url" target="_blank" rel="noopener" class="btn btn-sm btn-outline-primary">
                    Ver en FE
                  </a>
                  <span v-else class="text-muted small">Configurar FACTURACION_ELECTRONICA_FRONTEND_URL</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6 mb-3">Inutilizar rango de numeración</h3>
        <form class="row g-3" @submit.prevent="submitInutilizar">
          <div class="col-md-3">
            <label class="form-label">Establecimiento</label>
            <input v-model="inutilizarForm.establecimiento" class="form-control" type="text" maxlength="3" required />
          </div>
          <div class="col-md-3">
            <label class="form-label">Punto de expedición</label>
            <input v-model="inutilizarForm.puntoExpedicion" class="form-control" type="text" maxlength="3" required />
          </div>
          <div class="col-md-3">
            <label class="form-label">Desde número</label>
            <input v-model="inutilizarForm.desdeNumero" class="form-control" type="text" required />
          </div>
          <div class="col-md-3">
            <label class="form-label">Hasta número</label>
            <input v-model="inutilizarForm.hastaNumero" class="form-control" type="text" required />
          </div>
          <div class="col-md-8">
            <label class="form-label">Motivo</label>
            <input v-model="inutilizarForm.motivo" class="form-control" type="text" maxlength="150" required />
          </div>
          <div class="col-md-4 d-flex align-items-end">
            <button class="btn btn-dark" :disabled="submittingInutilizar">
              {{ submittingInutilizar ? 'Enviando...' : 'Inutilizar rango' }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <div class="card shadow-sm">
      <div class="card-body">
        <h3 class="h6 mb-3">Cancelar envío (cancel-send)</h3>
        <p class="text-muted small">
          Revierte a <code>PENDING</code> una venta en cola <code>QUEUED_BATCH</code> antes de que se transmita a SIFEN.
          No aplica a ventas ya transmitidas o rechazadas.
        </p>
        <form class="row g-3" @submit.prevent="submitCancelSend">
          <div class="col-md-3">
            <label class="form-label">ID de venta</label>
            <input v-model="cancelSendForm.idVenta" class="form-control" type="number" min="1" required />
          </div>
          <div class="col-md-6">
            <label class="form-label">Motivo</label>
            <input v-model="cancelSendForm.motivo" class="form-control" type="text" maxlength="500" required />
          </div>
          <div class="col-md-3 d-flex align-items-end">
            <button class="btn btn-outline-dark" :disabled="submittingCancelSend">
              {{ submittingCancelSend ? 'Enviando...' : 'Cancelar envío' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </section>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import maintenanceFiscalService from '@/services/maintenanceFiscalService'

const loadingAtencion = ref(false)
const submittingInutilizar = ref(false)
const submittingCancelSend = ref(false)
const errorMessage = ref('')
const atencion = ref([])

const inutilizarForm = reactive({
  establecimiento: '001',
  puntoExpedicion: '001',
  desdeNumero: '',
  hastaNumero: '',
  motivo: ''
})

const cancelSendForm = reactive({
  idVenta: '',
  motivo: ''
})

function formatDate(value) {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('es-PY')
  } catch {
    return value
  }
}

async function loadAtencion() {
  loadingAtencion.value = true
  errorMessage.value = ''
  try {
    atencion.value = await maintenanceFiscalService.listAtencionFiscal() || []
  } catch (error) {
    console.error('Error cargando ventas con atencion fiscal', error)
    errorMessage.value = error.userMessage || 'No se pudo cargar el listado de atención fiscal'
  } finally {
    loadingAtencion.value = false
  }
}

async function submitInutilizar() {
  submittingInutilizar.value = true
  try {
    await maintenanceFiscalService.inutilizarNumeracion({ ...inutilizarForm })
    window.alert('Rango inutilizado correctamente en facturación electrónica')
    inutilizarForm.desdeNumero = ''
    inutilizarForm.hastaNumero = ''
    inutilizarForm.motivo = ''
  } catch (error) {
    console.error('Error inutilizando numeracion', error)
    window.alert(error.userMessage || error?.response?.data?.message || 'No se pudo inutilizar el rango')
  } finally {
    submittingInutilizar.value = false
  }
}

async function submitCancelSend() {
  submittingCancelSend.value = true
  try {
    const result = await maintenanceFiscalService.cancelSend(cancelSendForm.idVenta, { motivo: cancelSendForm.motivo })
    window.alert(`Venta ${result?.id_venta} revertida a ${result?.new_status || 'PENDING'}`)
    cancelSendForm.idVenta = ''
    cancelSendForm.motivo = ''
  } catch (error) {
    console.error('Error en cancel-send', error)
    window.alert(error.userMessage || error?.response?.data?.message || 'No se pudo cancelar el envío')
  } finally {
    submittingCancelSend.value = false
  }
}

onMounted(loadAtencion)
</script>
